import { validateEmail } from "../lib/validate-email";
import type { SubscribePort } from "../adapters/subscribe-port";

// Cloudflare-published test site key. Always passes when paired with the test
// secret on the server. https://developers.cloudflare.com/turnstile/troubleshooting/testing/
const DEFAULT_TURNSTILE_SITE_KEY = "1x00000000000000000000AA";

export type TurnstileTokenGetter = (
  container: HTMLElement,
  siteKey: string,
) => Promise<string>;

export interface MountSubscribeOptions {
  turnstileSiteKey?: string;
  getTurnstileToken?: TurnstileTokenGetter;
}

interface TurnstileRenderOptions {
  sitekey: string;
  size?: "normal" | "compact" | "invisible" | "flexible";
  callback?: (token: string) => void;
  "error-callback"?: () => void;
  "expired-callback"?: () => void;
  "timeout-callback"?: () => void;
  appearance?: "always" | "execute" | "interaction-only";
}

interface TurnstileAPI {
  render(container: HTMLElement | string, options: TurnstileRenderOptions): string;
  execute(widgetIdOrContainer: string | HTMLElement): void;
  reset(widgetIdOrContainer?: string | HTMLElement): void;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileAPI;
  }
}

export function mountSubscribeForm(
  root: HTMLElement,
  port: SubscribePort,
  options: MountSubscribeOptions = {},
): void {
  const siteKey = options.turnstileSiteKey ?? DEFAULT_TURNSTILE_SITE_KEY;
  const getToken = options.getTurnstileToken ?? defaultGetTurnstileToken;

  root.innerHTML = `
    <section class="subscribe-section" data-testid="subscribe-section">
      <h2 class="subscribe-heading">Want updates?</h2>
      <p class="subscribe-blurb">
        Get notified when something interesting changes.
      </p>
      <form class="subscribe-form" data-testid="subscribe-form" novalidate>
        <label class="subscribe-field">
          Your email
          <input
            name="subscribe-email"
            type="email"
            required
            autocomplete="email"
            inputmode="email"
            placeholder="you@example.com"
          />
        </label>
        <div
          class="subscribe-turnstile"
          data-testid="subscribe-turnstile"
        ></div>
        <button type="submit" class="subscribe-submit">Subscribe</button>
        <p class="subscribe-status" data-testid="subscribe-status" aria-live="polite"></p>
      </form>
    </section>
  `;

  const form = root.querySelector<HTMLFormElement>("[data-testid='subscribe-form']");
  const emailInput = root.querySelector<HTMLInputElement>("input[name='subscribe-email']");
  const statusEl = root.querySelector<HTMLElement>("[data-testid='subscribe-status']");
  const submitButton = root.querySelector<HTMLButtonElement>(".subscribe-submit");
  const turnstileEl = root.querySelector<HTMLElement>(".subscribe-turnstile");

  if (!form || !emailInput || !statusEl || !submitButton || !turnstileEl) return;

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    void handleSubmit();
  });

  async function handleSubmit(): Promise<void> {
    if (!emailInput || !statusEl || !submitButton) return;
    const validation = validateEmail(emailInput.value);
    if (!validation.ok) {
      setStatus(statusEl, "Enter a valid email.", "error");
      return;
    }

    setStatus(statusEl, "Subscribing…", "pending");
    submitButton.disabled = true;
    try {
      const turnstileToken = turnstileEl
        ? await getToken(turnstileEl, siteKey)
        : "";
      const result = await port.submit({
        email: emailInput.value.trim().toLowerCase(),
        turnstileToken,
      });
      if (result.ok) {
        setStatus(statusEl, "Thanks — you're subscribed.", "success");
        emailInput.value = "";
      } else {
        setStatus(
          statusEl,
          "Couldn't subscribe — please try again.",
          "error",
        );
      }
    } finally {
      submitButton.disabled = false;
    }
  }
}

const turnstileWidgetIdsByContainer = new WeakMap<HTMLElement, string>();
let pendingTokenResolver: ((token: string) => void) | null = null;

const defaultGetTurnstileToken: TurnstileTokenGetter = async (
  container,
  siteKey,
) => {
  // Wait up to 5 s for the Turnstile script to load (it's async-defer).
  for (let i = 0; i < 50; i++) {
    if (typeof window !== "undefined" && window.turnstile) break;
    await sleep(100);
  }
  const turnstile = typeof window === "undefined" ? undefined : window.turnstile;
  if (!turnstile) return "";

  let widgetId = turnstileWidgetIdsByContainer.get(container);
  if (widgetId === undefined) {
    try {
      widgetId = turnstile.render(container, {
        sitekey: siteKey,
        size: "invisible",
        callback: (token: string) => {
          const resolver = pendingTokenResolver;
          pendingTokenResolver = null;
          if (resolver) resolver(token);
        },
        "error-callback": () => {
          const resolver = pendingTokenResolver;
          pendingTokenResolver = null;
          if (resolver) resolver("");
        },
        "expired-callback": () => {
          const resolver = pendingTokenResolver;
          pendingTokenResolver = null;
          if (resolver) resolver("");
        },
      });
      turnstileWidgetIdsByContainer.set(container, widgetId);
    } catch {
      return "";
    }
  } else {
    try {
      turnstile.reset(widgetId);
    } catch {
      // ignore
    }
  }

  return new Promise<string>((resolve) => {
    pendingTokenResolver = resolve;
    // Hard cap so a stuck widget eventually returns control to the user.
    setTimeout(() => {
      if (pendingTokenResolver === resolve) {
        pendingTokenResolver = null;
        resolve("");
      }
    }, 10_000);
    try {
      turnstile.execute(widgetId);
    } catch {
      if (pendingTokenResolver === resolve) {
        pendingTokenResolver = null;
        resolve("");
      }
    }
  });
};

function setStatus(
  el: HTMLElement,
  message: string,
  kind: "pending" | "success" | "error",
): void {
  el.textContent = message;
  el.dataset.kind = kind;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}
